import { AnimatePresence, motion } from 'framer-motion'
import { CircleIconButton } from '@/UIComponents2/CircleIconButton'
import { useCallback, useEffect, useState } from 'react'
import LeftArrowIcon from '@/CustomIcons/LeftArrowIcon'
import RightArrowIcon from '@/CustomIcons/RightArrowIcon'
import { cn } from '@/utlis/cn'

const MAX_VISIBLE_NUMBER_OF_CARDS = 3

// Directions to go through the carousel
enum DIRECTION {
    FORWARD,
    BACKWARD,
}

// Our basic card stack is a top, middle, bottom approach. Given they are absolutely positioned,
// this defines each cards location and basic styles such as scale, color, and opacity.
// Also defines the transition duration and method.
const CARD_LOCATION_STYLES = {
    TOP: {
        x: 0,
        scale: 1,
        opacity: 1,
        transition: {
            duration: 0.6,
            ease: [0.4, 0, 0.2, 1],
        },
        filter: 'none',
        zIndex: 2,
    },
    MIDDLE: {
        x: '7.5%',
        scale: 0.9,
        opacity: 1,
        filter: 'brightness(0.8)  blur(1px)',
        transition: {
            duration: 0.6,
            ease: [0.4, 0, 0.2, 1],
        },
        zIndex: 1,
    },
    BOTTOM: {
        x: '14.5%',
        scale: 0.8,
        opacity: 1,
        filter: 'brightness(0.6)  blur(2px)',
        transition: {
            duration: 0.6,
            ease: [0.4, 0, 0.2, 1],
        },
        zIndex: 0,
    },
    OFFSCREEN: {
        x: -300,
        scale: 1,
        opacity: 0,
        transition: {
            duration: 0.2,
            ease: [0.4, 0, 0.8, 1],
        },
        zIndex: 3,
    },
    HIDDEN: {
        x: 0,
        scale: 0.5,
        opacity: 0,
        transition: {
            duration: 0.6,
            ease: [0.4, 0, 0.2, 1],
        },
    },
}

// Variants to pass to the framer-motion components that can be referenced byname for animation updates.
const CARD_ANIMATION_VARIANTS = {
    TOP_CARD: CARD_LOCATION_STYLES.TOP,
    MIDDLE_CARD: CARD_LOCATION_STYLES.MIDDLE,
    BOTTOM_CARD: CARD_LOCATION_STYLES.BOTTOM,
    HIDDEN_CARD: CARD_LOCATION_STYLES.HIDDEN,
    TOP_CARD_EXIT: (custom: { direction: DIRECTION; ready: boolean }) => {
        if (!custom.ready) return {}
        return custom.direction === DIRECTION.FORWARD ? CARD_LOCATION_STYLES.OFFSCREEN : {}
    },
    TOP_CARD_IN: (custom: { direction: DIRECTION; ready: boolean }) => {
        if (!custom.ready) return {}
        return custom.direction === DIRECTION.FORWARD ? CARD_LOCATION_STYLES.MIDDLE : CARD_LOCATION_STYLES.OFFSCREEN
    },
    MIDDLE_CARD_IN: (custom: { direction: DIRECTION; ready: boolean }) => {
        if (!custom.ready) return {}
        return custom.direction === DIRECTION.FORWARD ? CARD_LOCATION_STYLES.BOTTOM : CARD_LOCATION_STYLES.TOP
    },
    BOTTOM_CARD_IN: (custom: { direction: DIRECTION; ready: boolean }) => {
        if (!custom.ready) return {}
        return custom.direction === DIRECTION.FORWARD ? CARD_LOCATION_STYLES.HIDDEN : CARD_LOCATION_STYLES.MIDDLE
    },
}

type CARD_ANIMATION = keyof typeof CARD_ANIMATION_VARIANTS

// What to animate a card to based on position.
function getAnimationTo(cardPosition: number): CARD_ANIMATION {
    switch (cardPosition) {
        case 0:
            return 'TOP_CARD'
        case 1:
            return 'MIDDLE_CARD'
        case 2:
            return 'BOTTOM_CARD'
        default:
            return 'HIDDEN_CARD'
    }
}

// What to animate a card from based on position.
function getAnimationFrom(cardPosition: number): CARD_ANIMATION | boolean {
    switch (cardPosition) {
        case 0:
            return 'TOP_CARD_IN'
        case 1:
            return 'MIDDLE_CARD_IN'
        case 2:
            return 'BOTTOM_CARD_IN'
        default:
            return false
    }
}

// For now, just a constant exit animation. Could change if we desired.
function getExitAnimation(): CARD_ANIMATION {
    return 'TOP_CARD_EXIT'
}

type CarouselProps = {
    contents: React.ReactNodeArray
    className?: string
}

type CarouselCard = {
    id: string
    content: React.ReactNode
}

const cardHeight = 'h-[230px] lg:h-[300px]'
const cardStyle = 'overflow-hidden'
const cardBorder = 'rounded-[26px]'
const cardBackgroundBase = 'backdrop-blur-lg bg-[rgba(0,0,0,0.75)]'

export default function Carousel({ contents, className }: CarouselProps) {
    const [cards, setCards] = useState<CarouselCard[]>([])
    const [direction, setDirection] = useState<DIRECTION>(DIRECTION.FORWARD)
    const [currentIndex, setCurrentIndex] = useState(0)
    const [ready, setReady] = useState(false)

    useEffect(() => {
        const updatedCards: Array<CarouselCard> = []
        for (const [index, card] of contents.entries()) {
            if (card !== null) {
                updatedCards.push({ id: `card-content-${index}`, content: card })
            }
        }
        setCards(updatedCards)
        // Small hack - wait 50ms before allowing animations to begin.
        // Due to the overly complex nature of the carousel I built
        // - I don't want initial animations firing - and the AnimatePresence component is not preventing it.
        setTimeout(() => {
            setReady(true)
        }, 50)
    }, [contents])

    const getVisibleCards = useCallback(() => {
        const visible = []
        for (let i = 0; i < Math.min(MAX_VISIBLE_NUMBER_OF_CARDS, cards.length); i++) {
            const index = (currentIndex + i) % cards.length
            visible.push({ ...cards[index], position: i })
        }
        return visible
    }, [currentIndex, cards])

    return (
        <div className={cn('relative', className)}>
            <div className="flex gap-4 pb-2 lg:pb-[17px]">
                <CircleIconButton
                    className="w-[35px] h-[35px] min-w-[35px]"
                    onClick={() => {
                        setCurrentIndex(currentIndex > 0 ? currentIndex - 1 : cards.length - 1)
                        setDirection(DIRECTION.BACKWARD)
                    }}
                >
                    <LeftArrowIcon />
                </CircleIconButton>
                <CircleIconButton
                    className="w-[35px] h-[35px] min-w-[35px]"
                    onClick={() => {
                        setCurrentIndex(currentIndex + 1)
                        setDirection(DIRECTION.FORWARD)
                    }}
                >
                    <RightArrowIcon />
                </CircleIconButton>
            </div>
            <div className={cn('flex w-full', cardHeight)}>
                <AnimatePresence mode="sync" initial={false} custom={{ direction, ready }}>
                    {getVisibleCards().map((card) =>
                        card.position === 0 ? (
                            // The key needs to change for the component to unmount and the exit animation to fire. We only "exit" the top card.
                            <motion.div
                                key={`${card.id}-${currentIndex}`}
                                custom={{ direction, ready }}
                                variants={CARD_ANIMATION_VARIANTS}
                                initial={getAnimationFrom(card.position)}
                                animate={getAnimationTo(card.position)}
                                exit={getExitAnimation()}
                                className={cn(
                                    cardStyle,
                                    cardBorder,
                                    cardBackgroundBase,
                                    cardHeight,
                                    'absolute w-[100%]'
                                )}
                            >
                                {card.content}
                            </motion.div>
                        ) : (
                            <motion.div
                                key={card.id}
                                variants={CARD_ANIMATION_VARIANTS}
                                custom={{ direction, ready }}
                                initial={getAnimationFrom(card.position)}
                                animate={getAnimationTo(card.position)}
                                className={cn(
                                    cardStyle,
                                    cardBorder,
                                    cardBackgroundBase,
                                    cardHeight,
                                    'absolute w-[100%]'
                                )}
                            >
                                {card.content}
                            </motion.div>
                        )
                    )}
                </AnimatePresence>
            </div>
        </div>
    )
}
